/*!
 * devextreme-angular
 * Version: 21.2.4
 * Build date: Mon Dec 06 2021
 *
 * Copyright (c) 2012 - 2021 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/* tslint:disable:max-line-length */


import { BrowserTransferStateModule } from '@angular/platform-browser';
import { TransferState } from '@angular/platform-browser';

import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';
import { dxFileManagerContextMenu, dxFileManagerToolbar } from 'devextreme/ui/file_manager';

import DxFileManager from 'devextreme/ui/file_manager';


import {
    DxComponent,
    DxTemplateHost,
    DxIntegrationModule,
    DxTemplateModule,
    NestedOptionHost,
    IterableDifferHelper,
    WatcherHelper
} from 'devextreme-angular/core';

import { DxoContextMenuModule } from 'devextreme-angular/ui/nested';
import { DxiItemModule } from 'devextreme-angular/ui/nested';
import { DxoItemViewModule } from 'devextreme-angular/ui/nested';
import { DxoDetailsModule } from 'devextreme-angular/ui/nested';
import { DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxoNotificationsModule } from 'devextreme-angular/ui/nested';
import { DxoPermissionsModule } from 'devextreme-angular/ui/nested';
import { DxoToolbarModule } from 'devextreme-angular/ui/nested';
import { DxiFileSelectionItemModule } from 'devextreme-angular/ui/nested';
import { DxoUploadModule } from 'devextreme-angular/ui/nested';




/**
 * The FileManager is a UI component that allows users to upload, select, and manage files and directories in different file storages.

 */
@Component({
    selector: 'dx-file-manager',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
})
export class DxFileManagerComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxFileManager;

    /**
     * Specifies the shortcut key that sets focus on the UI component.
    
     */
    @Input()
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether or not the UI component changes its state when interacting with a user.
    
     */
    @Input()
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Specifies the allowed upload file extensions.
    
     */
    @Input()
    get allowedFileExtensions(): Array<string> {
        return this._getOption('allowedFileExtensions');
    }
    set allowedFileExtensions(value: Array<string>) {
        this._setOption('allowedFileExtensions', value);
    }


    /**
     * Configures the context menu settings.
    
     */
    @Input()
    get contextMenu(): dxFileManagerContextMenu {
        return this._getOption('contextMenu');
    }
    set contextMenu(value: dxFileManagerContextMenu) {
        this._setOption('contextMenu', value);
    }


    /**
     * Specifies the path that is used when the FileManager is initialized.
    
     */
    @Input()
    get currentPath(): string {
        return this._getOption('currentPath');
    }
    set currentPath(value: string) {
        this._setOption('currentPath', value);
    }


    /**
     * Specifies an array of path keys to the current location.
    
     */
    @Input()
    get currentPathKeys(): Array<string> {
        return this._getOption('currentPathKeys');
    }
    set currentPathKeys(value: Array<string>) {
        this._setOption('currentPathKeys', value);
    }


    /**
     * Customizes columns in details view. Applies only if itemView.mode is &apos;details&apos;.
    
     */
    @Input()
    get customizeDetailColumns(): Function {
        return this._getOption('customizeDetailColumns');
    }
    set customizeDetailColumns(value: Function) {
        this._setOption('customizeDetailColumns', value);
    }


    /**
     * Allows you to provide custom icons to be used as thumbnails.
    
     */
    @Input()
    get customizeThumbnail(): Function {
        return this._getOption('customizeThumbnail');
    }
    set customizeThumbnail(value: Function) {
        this._setOption('customizeThumbnail', value);
    }


    /**
     * Specifies whether the UI component responds to user interaction.
    
     */
    @Input()
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the global attributes to be attached to the UI component&apos;s container element.
    
     */
    @Input()
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Specifies the file system provider.
    
     */
    @Input()
    get fileSystemProvider(): any {
        return this._getOption('fileSystemProvider');
    }
    set fileSystemProvider(value: any) {
        this._setOption('fileSystemProvider', value);
    }


    /**
     * Specifies a key of the initially or currently focused item.
    
     */
    @Input()
    get focusedItemKey(): string {
        return this._getOption('focusedItemKey');
    }
    set focusedItemKey(value: string) {
        this._setOption('focusedItemKey', value);
    }


    /**
     * Specifies whether the UI component can be focused using keyboard navigation.
    
     */
    @Input()
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies the UI component&apos;s height.
    
     */
    @Input()
    get height(): number | Function | string {
        return this._getOption('height');
    }
    set height(value: number | Function | string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the UI component.
    
     */
    @Input()
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the UI component changes its state when a user pauses on it.
    
     */
    @Input()
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Configures the file and directory view.
    
     */
    @Input()
    get itemView(): { details?: { columns?: Array<DevExpress.ui.dxFileManagerDetailsColumn | string> }, mode?: string, showFolders?: boolean, showParentFolder?: boolean } {
        return this._getOption('itemView');
    }
    set itemView(value: { details?: { columns?: Array<DevExpress.ui.dxFileManagerDetailsColumn | string> }, mode?: string, showFolders?: boolean, showParentFolder?: boolean }) {
        this._setOption('itemView', value);
    }


    /**
     * Configures notification settings.
    
     */
    @Input()
    get notifications(): { showPanel?: boolean, showPopup?: boolean } {
        return this._getOption('notifications');
    }
    set notifications(value: { showPanel?: boolean, showPopup?: boolean }) {
        this._setOption('notifications', value);
    }


    /**
     * Specifies actions that a user is allowed to perform on files and directories.
    
     */
    @Input()
    get permissions(): { copy?: boolean, create?: boolean, delete?: boolean, download?: boolean, move?: boolean, rename?: boolean, upload?: boolean } {
        return this._getOption('permissions');
    }
    set permissions(value: { copy?: boolean, create?: boolean, delete?: boolean, download?: boolean, move?: boolean, rename?: boolean, upload?: boolean }) {
        this._setOption('permissions', value);
    }


    /**
     * Specifies the root directory name.
    
     */
    @Input()
    get rootFolderName(): string {
        return this._getOption('rootFolderName');
    }
    set rootFolderName(value: string) {
        this._setOption('rootFolderName', value);
    }


    /**
     * Switches the UI component to a right-to-left representation.
    
     */
    @Input()
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Contains an array of initially or currently selected files and directories&apos; keys.
    
     */
    @Input()
    get selectedItemKeys(): Array<string> {
        return this._getOption('selectedItemKeys');
    }
    set selectedItemKeys(value: Array<string>) {
        this._setOption('selectedItemKeys', value);
    }


    /**
     * Specifies whether a user can select a single or multiple files and directories in the item view simultaneously.
    
     */
    @Input()
    get selectionMode(): string {
        return this._getOption('selectionMode');
    }
    set selectionMode(value: string) {
        this._setOption('selectionMode', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
    
     */
    @Input()
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Configures toolbar settings.
    
     */
    @Input()
    get toolbar(): dxFileManagerToolbar {
        return this._getOption('toolbar');
    }
    set toolbar(value: dxFileManagerToolbar) {
        this._setOption('toolbar', value);
    }


    /**
     * Configures upload settings.
    
     */
    @Input()
    get upload(): { chunkSize?: number, maxFileSize?: number } {
        return this._getOption('upload');
    }
    set upload(value: { chunkSize?: number, maxFileSize?: number }) {
        this._setOption('upload', value);
    }


    /**
     * Specifies whether the UI component is visible.
    
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the UI component&apos;s width.
    
     */
    @Input()
    get width(): number | Function | string {
        return this._getOption('width');
    }
    set width(value: number | Function | string) {
        this._setOption('width', value);
    }

    /**
    
     * A function that is executed when the UI component&apos;s content is ready and each time the content is changed.
    
    
     */
    @Output() onContentReady: EventEmitter<any>;

    /**
    
     * A function that is executed when a context menu item is clicked.
    
    
     */
    @Output() onContextMenuItemClick: EventEmitter<any>;

    /**
    
     * A function that is executed before a context menu is displayed.
    
    
     */
    @Output() onContextMenuShowing: EventEmitter<any>;

    /**
    
     * A function that is executed when the current directory is changed.
    
    
     */
    @Output() onCurrentDirectoryChanged: EventEmitter<any>;

    /**
    
     * A function that is executed when a directory is created.
    
    
     */
    @Output() onDirectoryCreated: EventEmitter<any>;

    /**
    
     * A function that is executed before a directory is created.
    
    
     */
    @Output() onDirectoryCreating: EventEmitter<any>;

    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    @Output() onDisposing: EventEmitter<any>;

    /**
    
     * A function that is executed when an error occurs.
    
    
     */
    @Output() onErrorOccurred: EventEmitter<any>;

    /**
    
     * A function that is executed when a file is successfully uploaded.
    
    
     */
    @Output() onFileUploaded: EventEmitter<any>;

    /**
    
     * A function that is executed before the file is uploaded.
    
    
     */
    @Output() onFileUploading: EventEmitter<any>;

    /**
    
     * A function that is executed when the focused item is changed.
    
    
     */
    @Output() onFocusedItemChanged: EventEmitter<any>;

    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    @Output() onInitialized: EventEmitter<any>;

    /**
    
     * A function that is executed when a file or directory is copied.
    
    
     */
    @Output() onItemCopied: EventEmitter<any>;

    /**
    
     * A function that is executed before a file or directory is copied.
    
    
     */
    @Output() onItemCopying: EventEmitter<any>;

    /**
    
     * A function that is executed when a file or directory is deleted.
    
    
     */
    @Output() onItemDeleted: EventEmitter<any>;

    /**
    
     * A function that is executed before a file or directory is deleted.
    
    
     */
    @Output() onItemDeleting: EventEmitter<any>;

    /**
    
     * A function that is executed before a file is downloaded.
    
    
     */
    @Output() onItemDownloading: EventEmitter<any>;

    /**
    
     * A function that is executed when a file or directory is moved.
    
    
     */
    @Output() onItemMoved: EventEmitter<any>;

    /**
    
     * A function that is executed before a file or directory is moved.
    
    
     */
    @Output() onItemMoving: EventEmitter<any>;

    /**
    
     * A function that is executed when a file or directory is renamed.
    
    
     */
    @Output() onItemRenamed: EventEmitter<any>;

    /**
    
     * A function that is executed before a file or directory is renamed.
    
    
     */
    @Output() onItemRenaming: EventEmitter<any>;

    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    @Output() onOptionChanged: EventEmitter<any>;

    /**
    
     * A function that is executed when the selected file is opened.
    
    
     */
    @Output() onSelectedFileOpened: EventEmitter<any>;

    /**
    
     * A function that is executed when a file system item is selected or selection is canceled.
    
    
     */
    @Output() onSelectionChanged: EventEmitter<any>;

    /**
    
     * A function that is executed when a toolbar item is clicked.
    
    
     */
    @Output() onToolbarItemClick: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() accessKeyChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() activeStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() allowedFileExtensionsChange: EventEmitter<Array<string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() contextMenuChange: EventEmitter<dxFileManagerContextMenu>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() currentPathChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() currentPathKeysChange: EventEmitter<Array<string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() customizeDetailColumnsChange: EventEmitter<Function>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() customizeThumbnailChange: EventEmitter<Function>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() disabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() elementAttrChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() fileSystemProviderChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() focusedItemKeyChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() focusStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() heightChange: EventEmitter<number | Function | string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hintChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hoverStateEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() itemViewChange: EventEmitter<{ details?: { columns?: Array<DevExpress.ui.dxFileManagerDetailsColumn | string> }, mode?: string, showFolders?: boolean, showParentFolder?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() notificationsChange: EventEmitter<{ showPanel?: boolean, showPopup?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() permissionsChange: EventEmitter<{ copy?: boolean, create?: boolean, delete?: boolean, download?: boolean, move?: boolean, rename?: boolean, upload?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() rootFolderNameChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() rtlEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectedItemKeysChange: EventEmitter<Array<string>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() selectionModeChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() tabIndexChange: EventEmitter<number>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() toolbarChange: EventEmitter<dxFileManagerToolbar>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() uploadChange: EventEmitter<{ chunkSize?: number, maxFileSize?: number }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() widthChange: EventEmitter<number | Function | string>;







    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper,
            optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'contextMenuItemClick', emit: 'onContextMenuItemClick' },
            { subscribe: 'contextMenuShowing', emit: 'onContextMenuShowing' },
            { subscribe: 'currentDirectoryChanged', emit: 'onCurrentDirectoryChanged' },
            { subscribe: 'directoryCreated', emit: 'onDirectoryCreated' },
            { subscribe: 'directoryCreating', emit: 'onDirectoryCreating' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'errorOccurred', emit: 'onErrorOccurred' },
            { subscribe: 'fileUploaded', emit: 'onFileUploaded' },
            { subscribe: 'fileUploading', emit: 'onFileUploading' },
            { subscribe: 'focusedItemChanged', emit: 'onFocusedItemChanged' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'itemCopied', emit: 'onItemCopied' },
            { subscribe: 'itemCopying', emit: 'onItemCopying' },
            { subscribe: 'itemDeleted', emit: 'onItemDeleted' },
            { subscribe: 'itemDeleting', emit: 'onItemDeleting' },
            { subscribe: 'itemDownloading', emit: 'onItemDownloading' },
            { subscribe: 'itemMoved', emit: 'onItemMoved' },
            { subscribe: 'itemMoving', emit: 'onItemMoving' },
            { subscribe: 'itemRenamed', emit: 'onItemRenamed' },
            { subscribe: 'itemRenaming', emit: 'onItemRenaming' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'selectedFileOpened', emit: 'onSelectedFileOpened' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { subscribe: 'toolbarItemClick', emit: 'onToolbarItemClick' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'allowedFileExtensionsChange' },
            { emit: 'contextMenuChange' },
            { emit: 'currentPathChange' },
            { emit: 'currentPathKeysChange' },
            { emit: 'customizeDetailColumnsChange' },
            { emit: 'customizeThumbnailChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'fileSystemProviderChange' },
            { emit: 'focusedItemKeyChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'itemViewChange' },
            { emit: 'notificationsChange' },
            { emit: 'permissionsChange' },
            { emit: 'rootFolderNameChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'selectedItemKeysChange' },
            { emit: 'selectionModeChange' },
            { emit: 'tabIndexChange' },
            { emit: 'toolbarChange' },
            { emit: 'uploadChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {

        return new DxFileManager(element, options);
    }


    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('allowedFileExtensions', changes);
        this.setupChanges('currentPathKeys', changes);
        this.setupChanges('selectedItemKeys', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('allowedFileExtensions');
        this._idh.doCheck('currentPathKeys');
        this._idh.doCheck('selectedItemKeys');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
        super.clearChangedOptions();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }
}

@NgModule({
  imports: [
    DxoContextMenuModule,
    DxiItemModule,
    DxoItemViewModule,
    DxoDetailsModule,
    DxiColumnModule,
    DxoNotificationsModule,
    DxoPermissionsModule,
    DxoToolbarModule,
    DxiFileSelectionItemModule,
    DxoUploadModule,
    DxIntegrationModule,
    DxTemplateModule,
    BrowserTransferStateModule
  ],
  declarations: [
    DxFileManagerComponent
  ],
  exports: [
    DxFileManagerComponent,
    DxoContextMenuModule,
    DxiItemModule,
    DxoItemViewModule,
    DxoDetailsModule,
    DxiColumnModule,
    DxoNotificationsModule,
    DxoPermissionsModule,
    DxoToolbarModule,
    DxiFileSelectionItemModule,
    DxoUploadModule,
    DxTemplateModule
  ]
})
export class DxFileManagerModule { }
